import React, { useState } from "react";
import { Mail, MessageCircle, Copy, Check, School } from "lucide-react";

/**
 * "Ask my school to add this" — turns a parent who already likes the product
 * into the top of the school funnel. The pitch is pre-written so the parent
 * only has to forward it.
 */
const SITE = "https://stories.hikmahverses.com";

const SUBJECT = "A Qur'an programme I'd love our school to look at";

const BODY = `Assalamu alaikum,

My child has been using 600 Stories by HikmahVerses at home — 600 illustrated, narrated Qur'an stories, two a week, for ages 8-13. It has been the rare kind of screen time I actually feel good about.

I think it would work beautifully for our class. They offer schools a free pilot — one classroom, one term, no commitment — plus teacher accounts and a plain-language safeguarding summary you can share with the board.

You can see it here: ${SITE}/schools

Would you be open to a look?

Jazakum Allahu khayran`;

export default function AskMySchool() {
  const [copied, setCopied] = useState(false);

  const mailto = `mailto:?subject=${encodeURIComponent(SUBJECT)}&body=${encodeURIComponent(BODY)}`;
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(`${SUBJECT}\n\n${BODY}`)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(BODY);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      /* clipboard blocked — the mail and WhatsApp buttons still work */
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
      <div className="hv-card p-8 sm:p-12" data-testid="ask-my-school">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3">
              <div className="hv-icon-circle w-12 h-12 rounded-full flex items-center justify-center shrink-0">
                <School size={22} strokeWidth={2.5} />
              </div>
              <span className="hv-badge">Two taps</span>
            </div>

            <h2 className="hv-heading mt-5 text-3xl sm:text-4xl">
              <span className="text-hv-navy">Love it? Ask your school to</span>{" "}
              <span className="text-hv-teal">add it.</span>
            </h2>
            <p className="mt-4 text-hv-slate leading-relaxed max-w-xl">
              We&apos;ve written the message for you — the pitch, the free pilot, the safeguarding
              summary. Send it to a teacher or head of Islamic studies and we&apos;ll take it from there.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href={mailto} data-testid="ask-school-email" className="hv-btn-primary inline-flex items-center gap-2">
                <Mail size={18} strokeWidth={2.5} /> Send by email
              </a>
              <a
                href={whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ask-school-whatsapp"
                className="hv-btn-secondary inline-flex items-center gap-2"
              >
                <MessageCircle size={18} strokeWidth={2.5} /> Send on WhatsApp
              </a>
              <button
                type="button"
                onClick={copy}
                data-testid="ask-school-copy"
                className="hv-btn-secondary inline-flex items-center gap-2"
              >
                {copied ? <Check size={18} strokeWidth={2.5} /> : <Copy size={18} strokeWidth={2.5} />}
                {copied ? "Copied" : "Copy the message"}
              </button>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-2xl bg-[#FBF6E4] border border-[#E5DFC8] p-6" data-testid="ask-school-preview">
              <div className="text-[10px] uppercase tracking-[0.2em] text-hv-teal font-bold">Message preview</div>
              <div className="hv-heading text-hv-navy text-sm mt-2">{SUBJECT}</div>
              <p className="mt-3 text-hv-slate text-sm leading-relaxed whitespace-pre-line">
                {BODY.split("\n\n").slice(0, 3).join("\n\n")}
              </p>
              <div className="mt-2 text-hv-slate/70 text-xs">…</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
